import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { conformToMask } from 'text-mask-core';

export const MAX_SAFE_VALUE = 999999999999999; // Novecentos e noventa e nove trilhões novecentos e noventa e nove bilhões novecentos e noventa e nove milhões novecentos e noventa e nove mil novecentos e noventa e nove

type Rule = (string | RegExp)[] | string;
type RuleFunction = (str: any) => Rule;

export type CreateNumberMaskProps = {
  prefix?: string;
  suffix?: string;
  includeThousandsSeparator?: boolean;
  thousandsSeparatorSymbol?: string;
  allowDecimal?: boolean;
  decimalSymbol?: string;
  decimalLimit?: number;
  minDecimalLimit?: number;
  requireDecimal?: boolean;
  allowNegative?: boolean,
  allowLeadingZeroes?: boolean,
  integerLimit?: number;
}

export interface CurrencyConfig extends Intl.NumberFormatOptions {
  allowNegative?: boolean;
  locale?: string;
};

export type Masks =
  | Rule
  | RuleFunction
  | 'CPF'
  | 'cpf'
  | 'CNPJ'
  | 'cnpj'
  | 'DATE'
  | 'date'
  | 'TIME'
  | 'time'
  | 'COVENANT'
  | 'covenant'
  | 'BANK_SLIP'
  | 'bank_slip'
  | 'BANK_SLIP_COVENANT'
  | 'bank_slip_covenant'
  | 'PHONE'
  | 'phone'
  | 'CPF_CNPJ'
  | 'cpf_cnpj'
  | 'CURRENCY'
  | 'currency'
  | 'DATE_TIME'
  | 'date_time'
  | 'MONTH_DATE'
  | 'month_date'
  | 'DDD_PHONE'
  | 'ddd_phone'
  | 'CELL_PHONE'
  | 'cell_phone'
  | 'DDD_CELL_PHONE'
  | 'ddd_cell_phone'
  | 'LANDLINE_PHONE'
  | 'landline_phone'
  | 'DDD_LANDLINE_PHONE'
  | 'ddd_landline_phone'
  | 'CEP'
  | 'cep'
  | 'account'
  | 'ACCOUNT'
  | 'NUMBER'
  | 'number'
  | 'PERCENTAGE'
  | 'percentage'
  | 'INTEGER'
  | 'integer';

export function strToMask(str: string): Rule {
  return str.split('').map(char => (char === '9' ? /\d/ : char));
}

const DEFAULT_CONFIG = {
  guide: false,
};

const CPF = strToMask('999.999.999-99');
const CNPJ = strToMask('99.999.999/9999-99');
const COVENANT = strToMask(
  '99999999999-9 99999999999-9 99999999999-9 99999999999-9'
);
const BANK_SLIP = strToMask(
  '99999.99999 99999.999999 99999.999999 9 99999999999999'
);
const CELL_PHONE = strToMask('99999-9999');
const DDD_CELL_PHONE = strToMask('(99) 99999-9999');
const LANDLINE_PHONE = strToMask('9999-9999');
const DDD_LANDLINE_PHONE = strToMask('(99) 9999-9999');
const CEP = strToMask('99999-999');

const MASKS = {
  CPF,
  CNPJ,
  COVENANT,
  BANK_SLIP,
  CELL_PHONE,
  LANDLINE_PHONE,
  DDD_CELL_PHONE,
  DDD_LANDLINE_PHONE,
  CEP,
  ACCOUNT: (str: string): Rule => {
    if (!str) return '';
    if (str.length < 5) {
      return str;
    }
    return str.replace(/.$/, `-${str.slice(-1)}`);
  },
  NUMBER: (str: string): Rule => {
    return str.replace(/[^\d]/g, '');
  },
  BANK_SLIP_COVENANT: (str: string): Rule =>
    !str || str.startsWith('8') ? COVENANT : BANK_SLIP,
  PHONE: (str: string): Rule =>
    !str || str.length > 8 ? CELL_PHONE : LANDLINE_PHONE,
  CPF_CNPJ: (str: string): Rule => (!str || str.length > 11 ? CNPJ : CPF),
  DDD_PHONE: (str: string): Rule =>
    !str || str.length > 10 ? DDD_CELL_PHONE : DDD_LANDLINE_PHONE,
  TIME: (str: string | Date): Rule => {
    if (!str || typeof str === 'string') return strToMask('99:99');

    return str.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  },
  DATE: (str: string | Date) => {
    if (!str || typeof str === 'string') return strToMask('99/99/9999');

    return str.toLocaleDateString('pt-BR');
  },
  DATE_TIME: (str: string | Date) => {
    if (!str || typeof str === 'string') return strToMask('99/99/9999 99:99');

    return str.toLocaleString('pt-BR', {
      day: '2-digit',
      hour: '2-digit',
      year: 'numeric',
      month: '2-digit',
      minute: '2-digit',
    });
  },
  MONTH_DATE: (str: string | Date) => {
    if (!str || typeof str === 'string') return strToMask('99/9999');

    return str.toLocaleDateString('pt-BR', {
      month: '2-digit',
      year: 'numeric',
    });
  },
  CURRENCY: (str: string | number, config?: CurrencyConfig): Rule => {
    
    // troca caractere especial por espaço em branco comum
    const sanitizeCurrency = (value: string) => value.replace(' ', ' ');

    const isSafeValue = (value: number) => value >= Number(MAX_SAFE_VALUE * -1) && value <= MAX_SAFE_VALUE;

    const moneyMask = (value: string): string => {
      const hasMinus = value.includes('-');
      const hasPlus = value.includes('+');
      
      value = value
      .replace('.', '')
      .replace(',', '')
      .replace(/\D/g, '')
      .replace(/\W/g, '');

      const signal = config?.allowNegative && hasMinus && !hasPlus ? '-' : '';
      let floatValue = parseFloat(signal + (value ? value : 0));

      if(!isSafeValue(floatValue)) {
        floatValue = parseFloat(signal + MAX_SAFE_VALUE);
      }

      if (isNaN(floatValue)) return '';

      const options: CurrencyConfig = {
        minimumFractionDigits: 2,
        style: 'currency',
        currency: 'BRL',
        ...config
      };

      return sanitizeCurrency(
        new Intl.NumberFormat(options.locale ?? 'pt-BR', options).format(floatValue / Math.pow(10, config?.minimumFractionDigits ?? 2))
      );
    };

    if (typeof str === 'string' && !str) return '';

    if (typeof str === 'string') {
      return moneyMask(str);
    }

    return sanitizeCurrency(
      new Intl.NumberFormat(config?.locale ?? 'pt-BR', {
      minimumFractionDigits: 2,
      style: 'currency',
      currency: 'BRL',
      ...config})
      .format(str));
  },
  PERCENTAGE: (str: string | number, config?: CreateNumberMaskProps): Rule => {
    if (typeof str === 'string' && !str) return '';

    if (typeof str === 'number') {
      const isFloat = str % 1 !== 0;

      const percent = (str / 100).toLocaleString('pt-BR', {
        style: 'percent',
        minimumFractionDigits: isFloat ? 2 : 0,
        maximumFractionDigits: (!!config && !!config.decimalLimit) ? config.decimalLimit : 2,
      });

      return percent;
    }

    const generateNumberMask = () => {
      return createNumberMask({
        prefix: '',
        suffix: '%',
        allowDecimal: true,
        decimalSymbol: ',',
        integerLimit: 3,
        thousandsSeparatorSymbol: '.',
        ...config,
      });
    }

    if (config?.minDecimalLimit) {
      const buildMask = (integerLimit = 3, minDecimalLimit = 2) => {
        const maskValueBeforeComa = new Array(integerLimit).fill('9').join('');
        const maskValueAfterComa = new Array(minDecimalLimit).fill('9').join('');
        return maskValueBeforeComa + ',' + maskValueAfterComa + '%';
      }

      const getValueLengthWithComma = () => {
        const substringLength = str.substring(0, str.indexOf(',')).length;
        const valueLength = (substringLength <= integerLimit) ? substringLength : integerLimit;
        return valueLength;
      }
  
      const hasComma = str.includes(',');
      const integerLimit = config?.integerLimit ?? 3;
      const valueBeforeComma = hasComma ? getValueLengthWithComma() : integerLimit;
      const valueAfterComma = config?.minDecimalLimit;

      const mask = strToMask(buildMask(valueBeforeComma, valueAfterComma));

      const { conformedValue } = conformToMask(str, mask, {guide: false,...config});
      return conformedValue;
    }

    return generateNumberMask();
  },
  INTEGER: (str: string) => {
    return new Intl.NumberFormat('pt-BR').format(Number(str.replace(/[^\d]/g, '')));
  },
};

export default function createMask(conformToMask: any) {
  return function mask(str: any, mask: Masks, config?: CreateNumberMaskProps | CurrencyConfig): string {

    let enhancedMask =
      typeof mask === 'string' ? MASKS[mask.toUpperCase()] : mask;

    if (!enhancedMask) throw new Error('Unexpected mask');

    if (typeof enhancedMask === 'function') {
      enhancedMask = enhancedMask(str, config);
    }

    if (typeof enhancedMask === 'string') return enhancedMask;
    const { conformedValue } = conformToMask(String(str || ''), enhancedMask, {
      ...DEFAULT_CONFIG,
    });
    return conformedValue;
  };
}